import React from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";

/**
 * Single source of truth for every CTA / action control on the site.
 * Before this, each page hand-rolled its own button classes (ink-fill,
 * accent-fill, outline, ghost, pill — a dozen+ near-identical recipes).
 * Pick a `variant` + `size`; pass `to` for a router Link, `href` for a
 * plain anchor, or nothing for a <button>.
 */

const BASE_CLS =
  "inline-flex items-center justify-center rounded-md font-semibold transition select-none " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-figures-accent/40 dark:focus-visible:ring-brand-accentInv/40";

const SIZE_CLS = {
  sm: "px-3.5 py-1.5 text-xs gap-1.5",
  md: "px-5 py-2.5 text-sm gap-2",
  lg: "px-6 py-3 text-base gap-2.5",
};

const VARIANT_CLS = {
  // Solid ink fill — the site's default primary action.
  primary: classNames(
    "bg-figures-ink text-figures-paper border border-figures-ink",
    "hover:bg-transparent hover:text-figures-ink",
    "dark:bg-brand-displayInv dark:text-brand-bg dark:border-brand-displayInv",
    "dark:hover:bg-transparent dark:hover:text-brand-displayInv",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-figures-ink disabled:hover:text-figures-paper",
    "dark:disabled:hover:bg-brand-displayInv dark:disabled:hover:text-brand-bg"
  ),
  // Solid accent fill — reserved for the one standout action on a page
  // (publish, save, submit), not the default.
  accent: classNames(
    "bg-figures-accent text-figures-paper border border-figures-accent",
    "hover:opacity-90",
    "dark:bg-brand-accentInv dark:text-brand-bg dark:border-brand-accentInv",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
  ),
  // Outline — secondary action alongside a primary one.
  secondary: classNames(
    "bg-transparent text-figures-ink border border-figures-ink/30",
    "hover:bg-figures-ink hover:text-figures-paper hover:border-figures-ink",
    "dark:text-brand-displayInv dark:border-white/20",
    "dark:hover:bg-brand-displayInv dark:hover:text-brand-bg dark:hover:border-brand-displayInv",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-figures-ink"
  ),
  // No box at rest — for CTAs living on quiet surfaces (closing sections,
  // inline links styled as actions).
  ghost: classNames(
    "bg-transparent text-figures-ink border border-transparent",
    "hover:bg-figures-ink/5",
    "dark:text-brand-displayInv dark:hover:bg-white/5",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
  ),
  // Destructive actions only (delete, remove).
  danger: classNames(
    "bg-red-600 text-white border border-red-600",
    "hover:bg-red-700 hover:border-red-700",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
  ),
};

export function Button({
  variant = "primary",
  size = "md",
  arrow = false,
  to,
  href,
  type,
  className = "",
  children,
  ...rest
}) {
  const cls = classNames(BASE_CLS, VARIANT_CLS[variant], SIZE_CLS[size], className);
  const content = (
    <>
      {children}
      {arrow && (
        <span aria-hidden="true" className="-mr-0.5">
          →
        </span>
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cls} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button type={type || "button"} className={cls} {...rest}>
      {content}
    </button>
  );
}

const ICON_SIZE_CLS = {
  sm: "w-8 h-8",
  md: "w-9 h-9",
};

/**
 * Square icon-only control (close, zoom, theme toggle, mobile nav toggle).
 * Neutral at rest, deepens on hover — matches `.glass-header` /
 * `.auth-link` so icon controls read as part of the same chrome as the
 * text nav around them.
 */
export function IconButton({ size = "md", className = "", label, children, ...rest }) {
  const cls = classNames(
    "inline-grid shrink-0 place-items-center rounded-md",
    "text-figures-muted dark:text-brand-mutedInv",
    "hover:text-figures-ink hover:bg-figures-ink/5",
    "dark:hover:text-brand-displayInv dark:hover:bg-white/5",
    "transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-figures-accent/40 dark:focus-visible:ring-brand-accentInv/40",
    ICON_SIZE_CLS[size],
    className
  );
  return (
    <button type="button" aria-label={label} className={cls} {...rest}>
      {children}
    </button>
  );
}

/**
 * Standalone (non-fused-group) toggle — filter switches, mode switches.
 * For a *row* of related toggles that should visually fuse when adjacent
 * cells are both selected, use `segmentClass` instead (Standards /
 * AlignStandards filter rows); this is for one-off toggles.
 */
export function ToggleButton({ active = false, className = "", children, ...rest }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={classNames(
        "rounded-md px-2.5 py-1 text-xs font-medium transition",
        active ? "glass-btn-active" : "glass-btn",
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
